import { getSubstringAfterLastPeriod } from "./stringHelpers";
import { AxiosResponse } from "axios";

function extensionType(extension: string): string {
  if (extension === "json") return "application/json";
  if (extension === "pdf") return "application/pdf";
  if (extension === "docx")
    return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  if (extension === "pptx")
    return "application/vnd.openxmlformats-officedocument.presentationml.presentation";
  if (extension === "png") return "image/png";
  if (extension === "jpeg" || extension === "jpg") return "image/jpeg";
  if (extension === "md") return "text/markdown";
  if (extension === "zip") return "application/zip";
  return "";
}

function getBlob(response: AxiosResponse, fileName: string): Blob {
  const extension = getSubstringAfterLastPeriod(fileName);
  const mime = extensionType(extension);

  if (!response.data) {
    return new Blob([], { type: mime });
  }

  if (response.data instanceof Blob) {
    return response.data;
  }

  if (response.data instanceof ArrayBuffer) {
    return new Blob([response.data], { type: mime });
  }

  if (typeof response.data === "string") {
    return new Blob([response.data], { type: mime });
  }

  if (typeof response.data === "object" && !Array.isArray(response.data)) {
    const data = JSON.stringify(response.data);
    return new Blob([data], { type: mime });
  }

  return new Blob([response.data], { type: mime });
}

function extractFilenameFromContentDisposition(
  contentDisposition: string,
): string | null {
  const encodedFilenameMatch = contentDisposition.match(
    /filename\*=([^']+)''(.+)/,
  );

  if (encodedFilenameMatch && encodedFilenameMatch[2]) {
    return decodeURIComponent(encodedFilenameMatch[2]);
  }

  const filenameMatch = contentDisposition.match(
    /filename=["']?([^"';\n]+)["']?/,
  );

  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1];
  }

  return null;
}

const previewableExtensions = new Set([
  ".pdf",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
]);

export function isPreviewableFile(fileName: string): boolean {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex === -1) return false;
  return previewableExtensions.has(fileName.slice(dotIndex).toLowerCase());
}

export function downloadDocumentBlob(
  response: AxiosResponse,
  fileName: string,
) {
  const dataForUrl = getBlob(response, fileName);
  const fileURL = window.URL.createObjectURL(dataForUrl);

  const fileLink = document.createElement("a");
  fileLink.href = fileURL;

  const contentDisposition = response.headers?.["content-disposition"];

  if (contentDisposition) {
    const extractedFilename =
      extractFilenameFromContentDisposition(contentDisposition);

    if (extractedFilename) {
      fileName = extractedFilename;
    }
  }

  fileLink.setAttribute("download", fileName);
  document.body.appendChild(fileLink);
  fileLink.click();
  fileLink.remove();
  window.URL.revokeObjectURL(fileURL);
}

export function downloadChunks(chunks: unknown, documentId: number | string) {
  const pseudoResponse = {
    data: typeof chunks === "object" ? JSON.stringify(chunks, null, 2) : chunks,
  } as unknown as AxiosResponse;

  downloadDocumentBlob(pseudoResponse, `document-${documentId}-chunks.txt`);
}

export function formatPageRanges(pages: number[]): string {
  if (pages.length === 0) {
    return "";
  }
  const uniqueSorted = Array.from(new Set(pages)).sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = uniqueSorted[0];
  let end = uniqueSorted[0];

  for (let i = 1; i < uniqueSorted.length; i++) {
    if (uniqueSorted[i] === end + 1) {
      end = uniqueSorted[i];
    } else {
      ranges.push(start === end ? `${start}` : `${start}-${end}`);
      start = uniqueSorted[i];
      end = uniqueSorted[i];
    }
  }
  ranges.push(start === end ? `${start}` : `${start}-${end}`);
  return ranges.join(", ");
}
